// Import dependencies
import { unsafeCSS, css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { AutomatorLogItem } from '@automator/features/page-logs/components/log-item/_base';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import html-entities
import { decode } from 'html-entities';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-loop-action' )
export class AutomatorLoopAction extends AutomatorLogItem {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	// The status has a different format if it's inside a loop
	@property( { type: Array, attribute: false } ) accessor status = [];

	// eslint-disable-next-line require-jsdoc
	render() {
		// Check if the data is missing
		if ( this.isDataMissing ) {
			return this._templateMissingData;
		}

		return html`
			<div class="item">
				${ this._templateIntegrationIcon }
				${ this._templateSentence }

				<div class="item__details">
					${ this._templateItemID }
					${ this._templateHighlightedStatuses }
				</div>

				${ this._templateOpenSidebarButton }
			</div>
		`;
	}

	/**
	 * Renders a container that shows the highlighted statuses, like the errors
	 *
	 * @return {TemplateResult|undefined} The highlighted statuses
	 */
	get _templateHighlightedStatuses() {
		// The statuses we'll show in this summary
		const highlightedStatuses = [ 'completed-with-errors', 'skipped', 'in-progress' ];

		// Get the highlighted statuses
		const statuses = this.status.filter( statusGroup => highlightedStatuses.includes( statusGroup.status_id ) );

		if ( statuses.length === 0 ) {
			return;
		}

		return html`
			<div class="item-loop__highlighted-statuses">
				${ repeat(
					statuses,
					statusGroup => statusGroup.status_id,
					statusGroup => html`
						<uap-log-status 
							status-id=${ statusGroup.status_id }
							count-number=${ statusGroup.count }
							hide-label
							show-tooltip
						></uap-log-status>
					`
				) }
			</div>
		`;
	}

	/**
	 * Creates the object passed to the sidebar
	 *
	 * @return {Object} The sidebar content
	 */
	get sidebarContent() {
		return {
			title: escapeHTML( __( 'Action inside loop', 'uncanny-automator' ) ),
			summary: this.sidebarSummary,
			runs: this.sidebarRuns,
			miscellaneous: {
				/**
				 * When there is only a single run, Automator will typically hide the run's summary and attempt
				 * to display only the properties, which usually pertains to the fields. However, for actions
				 * executed within loops, it is necessary to always display the summary of each run, regardless
				 * of whether there is only one run.
				 *
				 * @type {Boolean} - Whether to always show the run's summary
				 */
				runsAlwaysShowSummaries: true,

				/**
				 * Pagination metadata for loop runs. Present when runs are paginated.
				 */
				pagination: this.runsPagination,
			}
		}
	}

	/**
	 * Computes pagination metadata from the run groups.
	 *
	 * @return {Object|null} Pagination info or null when not paginated.
	 */
	get runsPagination() {
		let totalItems = 0;
		let currentPage = 1;
		let totalPages = 1;
		let perPage = UncannyAutomatorBackend.logs.runsPerPage;

		this.runs.forEach( runGroup => {
			totalItems += runGroup.total ?? runGroup.runs.length;

			if ( runGroup.total_pages > 0 ) {
				currentPage = Math.max( currentPage, runGroup.page ?? 1 );
				totalPages = Math.max( totalPages, runGroup.total_pages );
				perPage = runGroup.per_page ?? perPage;
			}
		} );

		// No pagination needed when everything fits on one page.
		if ( totalPages <= 1 ) {
			return null;
		}

		return {
			currentPage,
			totalPages,
			totalItems,
			perPage,
			hasPrev: currentPage > 1,
			hasNext: currentPage < totalPages,
			actionId: this.id,
		};
	}

	/**
	 * Creates the summary for the sidebar
	 *
	 * @return {Object} Summary for the sidebar
	 */
	get sidebarSummary() {
		// Summary items
		const items = [];

		// Add status
		items.push( {
			label: escapeHTML( __( 'Status', 'uncanny-automator' ) ),
			value: repeat(
				this.status,
				status => status.status_id,
				status => html`
					<div>
						<uap-log-status 
							status-id=${ status.status_id }
							count-number=${ status.count }
						></uap-log-status>
					</div>
				`
			)
		} );

		return {
			title: html`
				<uap-log-integration-icon integration-id="${ this.integrationCode }" show-tooltip></uap-log-integration-icon>
				<uap-log-sentence 
					.sentence="${ this.titleHTML }"
					custom-name="${ this.titleCustomName || '' }"
				></uap-log-sentence>
			`,
			items: items
		}
	}

	/**
	 * Creates the array for the sidebar to render the action runs
	 *
	 * @return {Array} The array of runs
	 */
	get sidebarRuns() {
		if ( this.runs.length === 0 ) {
			return [];
		}

		const runs = [];

		// Runs are grouped in actions inside loops. For now, remove them from their groups
		this.runs.forEach( runGroup => {
			// Iterate each run inside
			runGroup.runs.forEach( run => {
				// Create a summary of each run
				const runSummary = [];

				// Add the run status
				runSummary.push( {
					label: escapeHTML( __( 'Status', 'uncanny-automator' ) ),
					value: html`
						<uap-log-status status-id="${ runGroup.status_id }"></uap-log-status>
					`
				} );

				// Add the date
				runSummary.push( {
					label: escapeHTML( __( 'Date', 'uncanny-automator' ) ),
					value: run.date
				} );

				// Check if there is an error message
				if ( ! isEmpty( run.result_message ) ) {
					runSummary.push( {
						label: escapeHTML( __( 'Notes', 'uncanny-automator' ) ),
						value: decode( run.result_message )
					} );
				}

				runs.push( {
					id: run.id,
					title: html`
						<uap-log-status status-id="${ runGroup.status_id }" hide-label></uap-log-status>
						${ run.run_identifier }
					`,
					summary: runSummary,
					properties: run.properties || [],
				} );
			} );
		} );

		return runs;
	}

	/**
	 * The message shown when the action data is missing
	 *
	 * @return {String} The message
	 */
	get dataMissingErrorMessage() {
		return escapeHTML( __( 'Note: The information about this action is unavailable because it was removed from the recipe.', 'uncanny-automator' ) );
	}
}
